import { useTranslation } from '@cobliteam/i18n';
import { Link, Tag, Typography } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { Fragment, type ReactNode } from 'react';
import { DialogSetupGuideShell, type SetupGuideAction } from './DialogSetupGuideShell';

export interface SetupGuidePageLinkItem {
  label: string;
  href?: string;
}

export interface SetupGuidePageLink {
  /** Breadcrumb trail to the full page — e.g. ["Operação", "Locais de interesse"]. */
  items: SetupGuidePageLinkItem[];
  /** SPA navigation for the linked items; `href` stays for middle-click / accessibility. */
  onNavigate?: () => void;
  /** Overrides the nav's aria-label. Defaults to the shared translation. */
  ariaLabel?: string;
}

export interface DialogSetupGuideSuccessProps {
  open: boolean;
  onClose: () => void;
  /** e.g. "Locais criados com sucesso!". */
  title: string;
  /** Optional progress Tag — e.g. "1/3 locais criados". */
  progressLabel?: string;
  /** Grey descriptive message under the check. */
  message: ReactNode;
  /** Breadcrumb link to the entity's full page. */
  pageLink?: SetupGuidePageLink;
  /** Left footer link to repeat the action — e.g. "Criar novo local". */
  repeatAction?: { label: string; onClick: () => void };
  /** Primary button — e.g. "Voltar para Guia". */
  primaryAction: SetupGuideAction;
  'data-testid'?: string;
}

/**
 * Reusable success screen for the Setup Guide flows: a check hero, an optional progress
 * tag, a message, and a breadcrumb link to the entity's full page. Composes
 * {@link DialogSetupGuideShell} at `size='sm'` so there is a single modal shell.
 */
export const DialogSetupGuideSuccess = ({
  open,
  onClose,
  title,
  progressLabel,
  message,
  pageLink,
  repeatAction,
  primaryAction,
  'data-testid': dataTestId,
}: DialogSetupGuideSuccessProps) => {
  const { t } = useTranslation('ui-business');

  return (
    <DialogSetupGuideShell
      open={open}
      onClose={onClose}
      title={title}
      size='sm'
      data-testid={dataTestId}
      // Map the repeat link into the shell's left footer slot; hide it when not provided.
      cancelLabel={repeatAction?.label}
      onCancel={repeatAction?.onClick}
      hideCancel={!repeatAction}
      primaryAction={primaryAction}
    >
      <div className='flex flex-col items-center gap-4 py-2 text-center'>
        {/* Check hero uses the primary token (blue), per this epic's Figma — not the success/green hero. */}
        <div className='flex h-16 w-16 items-center justify-center rounded-full bg-primary-3 text-primary'>
          <Icon name='check' size='xl' color='primary' aria-hidden />
        </div>
        {progressLabel && <Tag color='primary' size='sm' label={progressLabel} />}
        <Typography variant='text-md' className='text-gray-11'>
          {message}
        </Typography>
        {pageLink && pageLink.items.length > 0 && (
          <nav aria-label={pageLink.ariaLabel ?? t('dialogs.setup_guide.page_link_aria')} className='flex flex-wrap items-center justify-center gap-1'>
            {pageLink.items.map((item, index) => {
              const isLast = index === pageLink.items.length - 1;
              return (
                <Fragment key={item.label}>
                  {item.href ? (
                    <Link href={item.href} onClick={pageLink.onNavigate} preferTo>
                      {item.label}
                    </Link>
                  ) : (
                    <Typography variant='text-sm' className='text-gray-11'>
                      {item.label}
                    </Typography>
                  )}
                  {!isLast && <Icon name='chevron-right' size='sm' className='text-gray-10' aria-hidden />}
                </Fragment>
              );
            })}
          </nav>
        )}
      </div>
    </DialogSetupGuideShell>
  );
};
